/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { Alert, Box, Button, Divider, Typography } from '@mui/material';
import { saveAs } from 'file-saver';
import moment from 'moment';

const finalized = invoice => ['paid', 'open', 'void', 'uncollectible'].includes(invoice.status);
const money = (amount, currency) => {
  if (!Number.isSafeInteger(amount) || !currency) return 'Unavailable';
  try {
    const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() });
    return formatter.format(amount / (10 ** formatter.resolvedOptions().maximumFractionDigits));
  } catch { return 'Unavailable'; }
};
const date = timestamp => typeof timestamp === 'number' ? moment.unix(timestamp).format('MMM DD, YYYY') : 'Unavailable';
const safeUrl = value => {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password && (!url.port || url.port === '443') &&
      ['invoice.stripe.com', 'pay.stripe.com', 'files.stripe.com'].includes(url.hostname) ? value : null;
  } catch { return null; }
};
const rows = invoice => [
  [invoice.hasProration ? 'Amount before separate discounts' : 'Original amount', invoice.originalAmount],
  [invoice.hasProration ? 'Separately recorded discount' : 'Discount amount', invoice.discountAmount],
  ['Tax amount', invoice.taxAmount], ['Invoice total', invoice.total],
  ...(invoice.startingBalance ? [['Opening customer balance', invoice.startingBalance]] : []),
  ['Final payable amount', invoice.amountDue], ['Amount paid', invoice.amountPaid],
  ['Amount remaining', invoice.amountRemaining],
];
const prorationNote = 'Prorated amounts may already include subscription discounts. Separately recorded discount shows only additional discounts recorded on this invoice; zero does not mean the subscription had no discount.';
const escapeHtml = value => String(value ?? '').replace(/[&<>"']/g, char => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[char]));

export const billingStatement = invoice => `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'">
<title>Billing statement ${escapeHtml(invoice.number || invoice.id)}</title>
<style>body{font:16px sans-serif;max-width:800px;margin:40px auto;padding:16px}th,td{text-align:left;padding:8px;border-bottom:1px solid #ddd}table{width:100%} @media print{body{margin:0}}</style></head>
<body><h1>Subscription billing statement</h1>
<p>Invoice ${escapeHtml(invoice.number || invoice.id)} · ${escapeHtml(invoice.status)} · ${escapeHtml(date(invoice.created))}</p>
<p>${(invoice.descriptions || []).map(escapeHtml).join('<br>')}</p>
<table><caption>Amounts in ${escapeHtml((invoice.currency || '').toUpperCase())}</caption><tbody>
${rows(invoice).map(([label, value]) => `<tr><th scope="row">${escapeHtml(label)}</th><td>${escapeHtml(money(value, invoice.currency))}</td></tr>`).join('')}
</tbody></table>${invoice.hasProration ? `<p>${escapeHtml(prorationNote)}</p>` : ''}<p>Based on the issued invoice record; this statement is not a payment receipt.
Customer balance can change the final payable amount. Use your browser’s Print command to print or save as PDF.</p></body></html>`;

export default function SubscriptionInvoices({ invoices }) {
  const [downloadError, setDownloadError] = useState(false);
  const download = invoice => {
    try {
      const fileName = String(invoice.number || invoice.id).replace(/[^a-z0-9_-]/gi, '_');
      saveAs(new Blob([billingStatement(invoice)], { type: 'text/html;charset=utf-8' }), `billing-statement-${fileName}.html`);
      setDownloadError(false);
    } catch { setDownloadError(true); }
  };
  if (!Array.isArray(invoices)) return <Typography color="text.secondary">Invoice records are unavailable. Refresh after the billing service is updated.</Typography>;
  if (invoices.length === 0) return <Typography color="text.secondary">No invoices issued yet.</Typography>;
  return <Box sx={{ mt: 2 }}>
    <Typography variant="h6">Invoices and documents</Typography>
    {downloadError && <Alert severity="error">Could not download the billing statement. Please try again.</Alert>}
    {invoices.map(invoice => {
      const invoiceUrl = finalized(invoice) && safeUrl(invoice.invoiceUrl);
      const invoicePdf = finalized(invoice) && safeUrl(invoice.invoicePdf);
      const receiptUrls = finalized(invoice) ? (invoice.receiptUrls || [invoice.receiptUrl]).map(safeUrl).filter(Boolean) : [];
      return <Box key={invoice.id} component="section" aria-label={`Invoice ${invoice.number || invoice.id}`} sx={{ mt: 2 }}>
        <Divider sx={{ mb: 2 }} />
        <Typography fontWeight="bold">Invoice {invoice.number || invoice.id}</Typography>
        <Typography variant="body2">{date(invoice.created)} · {invoice.status || 'Status unavailable'}</Typography>
        {(invoice.descriptions || []).map((description, index) => <Typography variant="body2" key={index}>{description}</Typography>)}
        {rows(invoice).map(([label, value]) => <Box key={label} role="group" aria-label={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 0.5 }}>
          <Typography variant="body2">{label}</Typography>
          <Typography variant="body2" fontWeight={label === 'Final payable amount' ? 'bold' : undefined}>{money(value, invoice.currency)}</Typography>
        </Box>)}
        {invoice.hasProration && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{prorationNote}</Typography>}
        {invoice.startingBalance !== 0 && invoice.startingBalance != null && <Typography variant="caption">Customer balance can change the final payable amount.</Typography>}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          {invoiceUrl && <Button component="a" href={invoiceUrl} target="_blank" rel="noopener noreferrer">View invoice</Button>}
          {invoicePdf && <Button component="a" href={invoicePdf} target="_blank" rel="noopener noreferrer">Download invoice</Button>}
          {receiptUrls.map((url, index) => <Button key={url} component="a" href={url} target="_blank" rel="noopener noreferrer">View receipt{receiptUrls.length > 1 ? ` ${index + 1}` : ''}</Button>)}
          {finalized(invoice) && invoice.statementAvailable && !invoiceUrl && !invoicePdf && <Button onClick={() => download(invoice)}>Download billing statement</Button>}
          {!finalized(invoice) && <Typography color="text.secondary">Document pending</Typography>}
        </Box>
      </Box>;
    })}
  </Box>;
}
